import {
  getElevationMeasurementUnit,
  getMeasurementUnit,
  getSpeedMeasurementUnit,
  isMeasurementSystemMetric,
} from './general.helpers';

export interface WorkoutSample {
  time?: number;
  heart_rate?: number | null;
  elevation?: number | null;
  speed?: number | null;
  distance?: number | null;
}

export type WorkoutMetricKey = 'smoothHeartRate' | 'displayElevation' | 'displaySpeed' | 'displayPace';

export interface PreparedWorkoutSample {
  time: number;
  heart_rate: number | null;
  elevation: number | null;
  speed: number | null;
  distance: number | null;
  smoothHeartRate: number | null;
  displayElevation: number | null;
  displaySpeed: number | null;
  displayPace: number | null;
  displayDistance: number;
}

export const convertElevationMeasurement = (elevation: number): number => {
  return isMeasurementSystemMetric() ? elevation : elevation * 3.28084;
};

export const convertSpeedMeasurement = (speedInMPS: number): number => {
  return isMeasurementSystemMetric() ? speedInMPS * 3.6 : speedInMPS * 2.23694;
};

/** Convert meters to user's distance unit (km or mi) */
export const convertDistanceMeasurement = (distanceMeters: number): number => {
  return isMeasurementSystemMetric() ? distanceMeters / 1000 : distanceMeters / 1609.344;
};

const getMinMax = (samples: PreparedWorkoutSample[], key: WorkoutMetricKey): [number, number] => {
  const values = samples
    .map((sample) => sample[key])
    .filter((value): value is number => value != null && !Number.isNaN(Number(value)));

  if (!values.length) {
    return [0, 0];
  }

  return [Math.min(...values), Math.max(...values)];
};

export const formatWorkoutSampleTime = (time: number): string => {
  return new Date(time * 1000).toISOString().substring(time > 3600 ? 11 : 14, 19);
};

export const formatPaceValue = (paceMinutesPerUnit: number | null): string => {
  if (paceMinutesPerUnit == null || paceMinutesPerUnit <= 0 || !Number.isFinite(paceMinutesPerUnit)) {
    return '—';
  }
  const mins = Math.floor(paceMinutesPerUnit);
  const secs = Math.round((paceMinutesPerUnit - mins) * 60);
  if (secs === 60) {
    return `${mins + 1}:00`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const prepareWorkoutSamples = (samples: WorkoutSample[] | undefined): PreparedWorkoutSample[] => {
  if (!samples?.length) {
    return [];
  }

  const filtered = samples.filter(
    (sample) => sample.time && (sample.heart_rate != null || sample.elevation != null || sample.speed != null)
  );

  const filledHeartRate = filtered.reduce<(number | null)[]>((acc, sample, i) => {
    const value = sample.heart_rate;
    if (value != null) {
      acc.push(value);
    } else if (i === 0) {
      const firstNonNullHr = filtered.find((s) => s.heart_rate != null)?.heart_rate ?? null;
      acc.push(firstNonNullHr);
    } else {
      acc.push(acc[i - 1]);
    }
    return acc;
  }, []);

  const hasAnyDistance = filtered.some((sample) => sample.distance != null && !Number.isNaN(Number(sample.distance)));
  const hasAnySpeed = filtered.some((sample) => sample.speed != null && Number(sample.speed) > 0);

  let integratedDistanceMeters = 0;
  let lastDisplayDistance = 0;
  const startTime = filtered[0].time as number;

  return filtered.map((sample, i) => {
    const displaySpeed =
      sample.speed != null && !Number.isNaN(Number(sample.speed))
        ? convertSpeedMeasurement(Number(sample.speed))
        : null;

    const displayPace = displaySpeed != null && displaySpeed > 0 ? 60 / displaySpeed : null;

    let displayDistance: number;

    if (hasAnyDistance) {
      const rawDistance =
        sample.distance != null && !Number.isNaN(Number(sample.distance)) ? Number(sample.distance) : null;
      if (rawDistance != null) {
        displayDistance = convertDistanceMeasurement(rawDistance);
      } else {
        displayDistance = i === 0 ? 0 : lastDisplayDistance;
      }
    } else if (hasAnySpeed) {
      if (i > 0 && sample.speed != null && sample.speed > 0) {
        const prev = filtered[i - 1];
        const dt = (sample.time as number) - (prev.time as number);
        if (dt > 0) {
          integratedDistanceMeters += sample.speed * dt;
        }
      }
      displayDistance = convertDistanceMeasurement(integratedDistanceMeters);
    } else {
      displayDistance = ((sample.time as number) - startTime) / 60;
    }

    lastDisplayDistance = displayDistance;

    return {
      time: sample.time as number,
      heart_rate: sample.heart_rate ?? null,
      elevation: sample.elevation ?? null,
      speed: sample.speed ?? null,
      distance: sample.distance ?? null,
      smoothHeartRate: filledHeartRate[i],
      displayElevation:
        sample.elevation != null && !Number.isNaN(Number(sample.elevation))
          ? convertElevationMeasurement(Number(sample.elevation))
          : null,
      displaySpeed,
      displayPace,
      displayDistance,
    };
  });
};

export const hasMetricData = (samples: PreparedWorkoutSample[], key: WorkoutMetricKey): boolean => {
  return samples.some((sample) => sample[key] != null);
};

export const getMetricDomain = (samples: PreparedWorkoutSample[], key: WorkoutMetricKey): [number, number] => {
  const [min, max] = getMinMax(samples, key);
  if (min === max) {
    return [Math.max(min - 1, 0), max + 1];
  }
  // Pace: invert domain slightly padded so slower paces sit higher visually if needed
  if (key === 'displayPace') {
    const pad = (max - min) * 0.05 || 0.1;
    return [Math.max(min - pad, 0), max + pad];
  }
  return [min, max];
};

export const getAverageElevation = (samples: PreparedWorkoutSample[]): number | null => {
  const elevations = samples.map((sample) => sample.displayElevation).filter((value): value is number => value != null);

  if (!elevations.length) {
    return null;
  }

  return elevations.reduce((sum, value) => sum + value, 0) / elevations.length;
};

export const findPeakSample = (
  samples: PreparedWorkoutSample[],
  key: WorkoutMetricKey
): PreparedWorkoutSample | null => {
  let peak: PreparedWorkoutSample | null = null;
  samples.forEach((sample) => {
    const value = sample[key];
    if (value == null) {
      return;
    }
    if (peak == null || (peak[key] as number) < value) {
      peak = sample;
    }
  });
  return peak;
};

/** Lowest value for a metric (e.g. fastest pace = lowest min/unit). */
export const findTroughSample = (
  samples: PreparedWorkoutSample[],
  key: WorkoutMetricKey
): PreparedWorkoutSample | null => {
  let trough: PreparedWorkoutSample | null = null;
  samples.forEach((sample) => {
    const value = sample[key];
    if (value == null) {
      return;
    }
    if (trough == null || (trough[key] as number) > value) {
      trough = sample;
    }
  });
  return trough;
};

export const formatElevationTooltip = (elevation: number | null): string => {
  if (elevation == null) {
    return '—';
  }
  return `${elevation.toFixed(0)} ${getElevationMeasurementUnit()}`;
};

export const formatSpeedTooltip = (speed: number | null): string => {
  if (speed == null) {
    return '—';
  }
  return `${speed.toFixed(1)} ${getSpeedMeasurementUnit()}`;
};

export const formatPaceTooltip = (pace: number | null): string => {
  const formatted = formatPaceValue(pace);
  if (formatted === '—') {
    return '—';
  }
  return `${formatted} /${getMeasurementUnit()}`;
};

export const formatDistanceTick = (distance: number): string => {
  return `${distance.toFixed(distance >= 10 ? 0 : 1)}`;
};
